import PortalPrograms from '@classes/Programs'
import BlockedScheduling from '@components/BlockScheduling'
import EmptyLessons from '@components/EmptyLessons'
import EmptyFlexLessons from '@components/EmptyLessons/EmptyFlexLessons'
import EmptyState from '@components/EmptyState'
import Select from '@components/Select'
import WithUserTimeHOC from '@hocs/WithUserTime'
import { StoreProps } from '@interfaces/StoreState'
import LessonCount from '@layouts/Scheduling/ScheduleOptions/LessonCount'
import NotEnoughLessonsRemaining from '@layouts/Scheduling/ScheduleOptions/NotEnoughLessonsRemaining'
import { updateDashboardActiveProgram } from '@redux/actions/dashboard'
import { setScheduleTabTitle } from '@redux/actions/mySchedules'
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Row } from 'react-styled-flexboxgrid'
import OptionsBody from './OptionsBody'
import { ProgramLabel, ProgramSelect, StyledCol } from './style'
import PortalStudentProfile from '@classes/StudentProfile'
import OnlinePlacementTest from '@components/OnlinePlacementTest'
import COPT from '@classes/OnlinePlacemenTest'
import { getStoredMaterials } from '@utils/helpers/material'
import InvalidProgramStatus from './InvalidProgramStatus'
import { useIntl } from 'react-intl'

const ScheduleOptions = ({ getDateWithUserTimezone }) => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { portalPrograms, activeProgram, profile } = useSelector(({ userProfile, dashboard }: StoreProps) => ({
    portalPrograms: new PortalPrograms(
      userProfile?.info?.StudentProfile?.StudentProfileInfo?.ProgramStudentWrappers ?? []
    ),
    activeProgram: dashboard.activeProgram,
    profile: new PortalStudentProfile(userProfile.info.StudentProfile?.StudentProfileInfo),
  }))

  const programsDropdown = portalPrograms.studentProgramsSortedDropdown(
    getDateWithUserTimezone,
    false,
    portalPrograms.HasBoDProgram
  )
  const [currProgramId, setCurrentProgramId] = useState(activeProgram || programsDropdown[0]?.value)
  const [isOPTRequired, setIsOPTRequired] = useState(false)
  const currProgram = portalPrograms.getProgram(currProgramId)

  useEffect(() => {
    currProgram && dispatch(setScheduleTabTitle(currProgram.isFlexProgram() ? 'Book' : 'Schedule lessons'))
    if (currProgram) {
      const opt = new COPT(profile.student, undefined, profile.Programs[currProgramId])
      setIsOPTRequired(opt.IsRequired)

      localStorage.setItem(`${profile.SFId}-programid`, currProgramId)
      const materials = getStoredMaterials()
      const material = portalPrograms.getProgram(currProgramId)?.program?.Materials?.[0]?.LPID
      if (material) {
        materials[profile.SFId] = portalPrograms.getProgram(currProgramId)?.program?.Materials?.[0]?.LPID
        localStorage.setItem(`portal-selected-material`, JSON.stringify(materials))
      }
    }
  }, [currProgramId])

  const programs = profile?.PortalPrograms
  if (!programs?.IsMultiplePrograms) {
    const program = programs?.Programs.find((program) => program?.program?.ProgramType.includes('Berlitz On Demand'))
    if (program && program?.program?.ProgramId) {
      return (
        <InvalidProgramStatus
          {...program.program}
          customTitle={intl.formatMessage({
            id: 'You do not need to book any lessons for this course.',
            defaultMessage: 'You do not need to book any lessons for this course.',
          })}
          customBody={intl.formatMessage({
            id: 'Please contact your learning center for more information, or raise a case using the button below.',
            defaultMessage:
              'Please contact your learning center for more information, or raise a case using the button below.',
          })}
        />
      )
    }
  }

  if (!portalPrograms.Programs.length) {
    return (
      <EmptyState
        heading="Looks like your course hasn't been setup."
        img="/images/empty-events-24.svg"
        linkUrl="/support/contact-us/?id=technical-support"
        btnTxt="Request support"
      />
    )
  }

  if (portalPrograms.allProgramsNoLessonsRemaining()) {
    return portalPrograms.AreAllProgramsFlex ? (
      <EmptyFlexLessons top="md" programId={portalPrograms?.FlexPrograms?.[0]?.ProgramId} />
    ) : (
      <EmptyLessons top="md" />
    )
  }

  if (portalPrograms.programsNotEnoughLessonLeftPerSession() && programsDropdown.length) {
    return <NotEnoughLessonsRemaining {...currProgram.program} />
  }

  if (portalPrograms.allProgramsBlocks()) {
    return <BlockedScheduling />
  }

  if (!currProgram) {
    return <EmptyState heading="Oops, something went wrong." img="/images/bug.svg" />
  }

  return (
    <>
      <Row>
        <StyledCol xs={12}>
          <ProgramSelect>
            <ProgramLabel>
              <div>
                <FormattedMessage id={currProgram.isFlexProgram() ? 'Select your course' : 'Book a lesson for'} />:
              </div>
            </ProgramLabel>
            <Select
              label=""
              name="Program"
              value={currProgramId}
              onChange={(program) => {
                setCurrentProgramId(program)
                dispatch(updateDashboardActiveProgram(program))
              }}
              options={programsDropdown}
              disableMargin
              responsive
            />
            {currProgramId && <LessonCount programId={currProgramId} />}
          </ProgramSelect>
        </StyledCol>
      </Row>
      {isOPTRequired ? <OnlinePlacementTest showNotSetBanner /> : <OptionsBody programId={currProgramId} />}
    </>
  )
}

export default WithUserTimeHOC(React.memo(ScheduleOptions))
